import { insertImageIntoMarkdownEditor } from "@/components/markdown/imagePlugin/shared";
import type { EditableContentType } from "@/utils/content";
import { get, set } from "lodash";
import type { MaybeRefOrGetter } from "vue";
import { useToast } from "vue-toast-notification";

export function useContentEdit({
  handleCancel,
  contentId: contentIdProp,
  dataPath,
  contentType,
}: {
  contentId?: MaybeRefOrGetter<string>;
  dataPath?: MaybeRefOrGetter<string>;
  contentType: MaybeRefOrGetter<EditableContentType>;
  handleCancel: () => void;
}) {
  const contentId = computed(() => {
    return toValue(contentIdProp) || "";
  });
  const { data, isLoading, isError, error } = useSingleContentRequest({ contentId });
  const [isSidebarOpen, toggleSidebar] = useToggle(!isMobile.value);
  const { isPending, mutate } = useContentUpdateMutation();
  const $toast = useToast(defaultToastOptions);
  const { t } = withI18nHelpers();
  const queryClient = useQueryClient();
  const { getTaskByName, isLoading: isLoadingConfig } = useTaskConfigDataCore();
  const alternateContent = ref<string>();
  const regeneratedText = ref<string>("");

  const content = computed(
    () =>
      regeneratedText.value ||
      get(data.value, toValue(dataPath) || "resultData.content", "")
  );
  const images = ref<string[]>([]);
  const videos = ref<string[]>([]);

  const taskConfig = computed(() => {
    if (!data.value) return null;
    const taskType = data.value.taskType;
    return getTaskByName(taskType) ?? null;
  });

  const isSidebarSupported = computed(() => {
    if (!data.value) return false;
    return (
      taskConfig.value?.isContentEditable &&
      (taskConfig.value.canAddImages ||
        (taskConfig.value.isContentRegenerate && !!taskConfig.value.tone))
    );
  });

  const imagePath = computed(() => "images");
  const videoPath = computed(() => "videos");

  function acceptContent() {
    if (!alternateContent.value) return;
    regeneratedText.value = alternateContent.value;
    alternateContent.value = undefined;
  }

  function rejectContent() {
    alternateContent.value = undefined;
  }

  function arrayFromMarkdownList(markdown: string) {
    return markdown
      .split("\n")
      .filter(Boolean)
      .map(line => line.replace(/^\s*-\s*/, "").trim());
  }

  function saveContent(payload: string) {
    const _dataPath = toValue(dataPath);
    if (!data.value || !_dataPath) return;
    // clone data
    const newData = JSON.parse(JSON.stringify(data.value));
    // set data using path
    const update =
      toValue(contentType) === "array-list" ? arrayFromMarkdownList(payload) : payload;
    set(newData, _dataPath, update);
    newData.schedule = undefined;
    set(newData, imagePath.value, images.value);
    set(newData, videoPath.value, videos.value);
    // save updated data
    mutate(newData, {
      onSuccess(data) {
        $toast.success(t("contentUpdated"));
        updateSingleContentInQueryCache(contentId, data, queryClient);
        updateSingleContentListQueryCache(data.taskId, data, queryClient);
        queryClient.invalidateQueries({
          queryKey: ["scheduled-content"],
        });
        handleCancel();
      },
      onError() {
        $toast.error(t("contentUpdateFailed"));
      },
    });
  }

  function addImage(imageUrl: string) {
    if (!imageUrl) return;
    if (toValue(contentType) === "markdown") {
      insertImageIntoMarkdownEditor(imageUrl);
      return;
    }
    images.value = Array.from(new Set([...images.value, imageUrl]));
  }

  function removeImage(imageUrl: string) {
    if (!imageUrl) return;
    images.value = images.value.filter(image => image !== imageUrl);
  }

  function addVideo(videoUrl: string) {
    if (!videoUrl) return;
    videos.value = Array.from(new Set([...videos.value, videoUrl]));
  }

  function removeVideo(videoUrl: string) {
    if (!videoUrl) return;
    videos.value = videos.value.filter(video => video !== videoUrl);
  }

  watch(
    data,
    value => {
      if (!value) return;
      // get images from data
      const imageUrlsRaw = value.images || [];
      const imageUrls = Array.isArray(imageUrlsRaw)
        ? (imageUrlsRaw.map(enforceFilesDomain).filter(Boolean) as string[])
        : [];
      images.value = Array.from(new Set([...imageUrls, ...(images.value || [])]));

      // get videos from data
      const videoUrlsRaw = value.videos || [];
      const videoUrls = Array.isArray(videoUrlsRaw)
        ? (videoUrlsRaw.map(enforceFilesDomain).filter(Boolean) as string[])
        : [];
      videos.value = Array.from(new Set([...videoUrls, ...(videos.value || [])]));
    },
    {
      immediate: true,
    }
  );

  onBeforeMount(() => {
    images.value = get(data.value, imagePath.value, []);
    videos.value = get(data.value, videoPath.value, []);
  });

  return {
    isSidebarOpen,
    toggleSidebar,
    isSidebarSupported,
    data,
    isLoading,
    isError,
    error,
    content,
    images,
    videos,
    alternateContent,
    regeneratedText,
    isUpdating: isPending,
    saveContent,
    addImage,
    addVideo,
    removeImage,
    removeVideo,
    acceptContent,
    rejectContent,
    taskConfig,
    isLoadingConfig,
  };
}
